// App header: title + subtitle, "Last synced HH:MM ICT" with a `sampled` badge,
// the billing-cycle indicator (Mon YYYY · day N / D (X% of cycle)), plus refresh,
// dark toggle, and logout. Matches the mockup header layout/hierarchy.
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../lib/auth-store";
import { useThemeStore } from "../lib/theme";
import { billingCycle } from "../lib/format";

interface Props {
  lastSynced: string | null;
  onRefresh: () => void;
  refreshing: boolean;
  /** Show the "sampled" badge only when the worker reports sampled metrics. */
  sampled?: boolean;
}

/** ISO → "07:00:12 ICT" (Asia/Bangkok). "—" when unknown. */
function ictTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const t = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "Asia/Bangkok",
  });
  return `${t} ICT`;
}

export function Header({ lastSynced, onRefresh, refreshing, sampled }: Props) {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { theme, toggle } = useThemeStore();

  const cycle = billingCycle(new Date());

  async function onLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <header className="sticky top-0 z-10 border-b border-gray-200 bg-white/90 backdrop-blur dark:border-gray-700 dark:bg-gray-900/90">
      <div className="mx-auto flex max-w-6xl flex-wrap items-start justify-between gap-3 px-4 py-3">
        {/* Left: title + subtitle */}
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            BuildX — System Health &amp; Cloudflare Cost
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Live health &amp; Cloudflare cost — buildx-pytha · buildx-onsite · buildx-price
          </p>
        </div>

        {/* Right: sync line + billing cycle, then controls */}
        <div className="flex items-center gap-4">
          <div className="hidden text-right text-xs text-gray-500 sm:block dark:text-gray-400">
            <div className="flex items-center justify-end gap-1.5">
              <span>
                Last synced{" "}
                <span className="font-mono text-gray-700 dark:text-gray-300">
                  {ictTime(lastSynced)}
                </span>
              </span>
              {sampled === true && (
                <span
                  className="rounded bg-gray-100 px-1.5 py-px text-[10px] font-semibold text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                  title="Some Cloudflare metrics this month are sampled — values are estimates"
                >
                  sampled
                </span>
              )}
            </div>
            <div className="text-[11px] text-gray-400 dark:text-gray-500">
              {cycle.label} · day {cycle.day} / {cycle.daysInMonth} (
              {Math.round(cycle.pctOfCycle * 100)}% of cycle)
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              className="rounded-lg border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
              aria-label="Refresh data"
            >
              {refreshing ? "…" : "↻"}
            </button>
            <button
              type="button"
              onClick={toggle}
              aria-label="Toggle dark mode"
              className="rounded-lg border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              {theme === "dark" ? "☀️" : "🌙"}
            </button>
            {user && (
              <span className="hidden text-sm text-gray-500 lg:inline dark:text-gray-400">
                {user}
              </span>
            )}
            <button
              type="button"
              onClick={onLogout}
              className="rounded-lg bg-gray-900 px-3 py-1 text-sm font-medium text-white hover:bg-gray-700 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-300"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
